import React, { useCallback, useEffect, useMemo, useState } from 'react';

const API = process.env.REACT_APP_API_URL;

function apiUrl(path) {
  return `${API}${path}`;
}

async function apiRequest(path, options = {}) {
  const token = localStorage.getItem('accessToken');
  const response = await fetch(apiUrl(path), {
    ...options,
    headers: {
      ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  const contentType = response.headers.get('content-type') || '';
  const payload = contentType.includes('application/json') ? await response.json() : null;
  if (!response.ok) throw new Error(payload?.detail || `Request failed (${response.status})`);
  return payload;
}

function localDate() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

function formatDate(value) {
  if (!value) return '—';
  const [year, month, day] = String(value).slice(0, 10).split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function FilingControls({ caseId, packet, filingEnabled, credentials, onChanged }) {
  const [filing, setFiling] = useState(packet.latest_filing || null);
  const [flags, setFlags] = useState({ filing: false, redaction: false, service: false });
  const [confirmation, setConfirmation] = useState('');
  const [docketNumber, setDocketNumber] = useState('');
  const [error, setError] = useState('');
  const [working, setWorking] = useState(false);

  useEffect(() => setFiling(packet.latest_filing || null), [packet.latest_filing]);

  useEffect(() => {
    if (!filing?.id || !['queued', 'in_progress'].includes(filing.status)) return undefined;
    const timer = window.setInterval(async () => {
      try {
        const current = await apiRequest(`/firm/cmecf-bnc-compliance/${filing.id}/`);
        setFiling(current);
        if (!['queued', 'in_progress'].includes(current.status)) onChanged?.();
      } catch (requestError) {
        setError(requestError.message);
      }
    }, 2500);
    return () => window.clearInterval(timer);
  }, [filing?.id, filing?.status, onChanged]);

  const start = async () => {
    setWorking(true); setError('');
    try {
      const current = await apiRequest(
        `/case/${caseId}/bnc-compliance/packets/${packet.id}/file/`,
        {
          method: 'POST',
          body: JSON.stringify({
            filing_acknowledged: flags.filing,
            redaction_acknowledged: flags.redaction,
            service_acknowledged: flags.service,
            confirmation_text: confirmation,
          }),
        },
      );
      setFiling(current);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setWorking(false);
    }
  };

  const resolve = async (outcome) => {
    setWorking(true); setError('');
    try {
      const current = await apiRequest(`/firm/cmecf-bnc-compliance/${filing.id}/resolve/`, {
        method: 'POST', body: JSON.stringify({ outcome, docket_number: docketNumber }),
      });
      setFiling(current); onChanged?.();
    } catch (requestError) {
      setError(requestError.message);
    } finally { setWorking(false); }
  };

  if (filing?.status === 'succeeded') {
    return (
      <div className="mt-3 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
        Filed{filing.docket_number ? ` as docket ${filing.docket_number}` : ''}. {filing.status_message}
      </div>
    );
  }
  if (filing && ['queued', 'in_progress'].includes(filing.status)) {
    return <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">{filing.status_message}</div>;
  }
  if (filing?.status === 'review_required') {
    return (
      <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
        <p className="font-semibold">Check the live docket before continuing.</p>
        <p className="mt-1">{filing.error_message}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <input value={docketNumber} onChange={(event) => setDocketNumber(event.target.value)} placeholder="Notice docket number" className="rounded border border-amber-300 px-3 py-2" />
          <button type="button" onClick={() => resolve('completed')} disabled={working} className="rounded bg-green-700 px-3 py-2 font-semibold text-white">Confirm filed</button>
          <button type="button" onClick={() => resolve('not_completed')} disabled={working} className="rounded border border-amber-400 bg-white px-3 py-2 font-semibold">Confirm not filed</button>
        </div>
        {error && <p className="mt-2 font-medium text-red-700">{error}</p>}
      </div>
    );
  }

  return (
    <details className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3">
      <summary className="cursor-pointer text-sm font-semibold text-red-800">File this notice electronically</summary>
      {!filingEnabled && <p className="mt-2 text-sm text-red-700">Live CM/ECF filing is disabled on the server.</p>}
      {!credentials?.ready_to_file && <p className="mt-2 text-sm text-red-700">Complete and enable CM/ECF Automatic Filing on Account Management first.</p>}
      {filing?.error_message && <p className="mt-2 text-sm text-red-700">Last attempt: {filing.error_message}</p>}
      <div className="mt-3 space-y-2 text-sm text-slate-800">
        <label className="flex gap-2"><input type="checkbox" checked={flags.service} onChange={(event) => setFlags({ ...flags, service: event.target.checked })} />I confirm every listed recipient was served by the method and on the date stated in the notice.</label>
        <label className="flex gap-2"><input type="checkbox" checked={flags.redaction} onChange={(event) => setFlags({ ...flags, redaction: event.target.checked })} />I reviewed the PDF and confirmed that it complies with court redaction requirements.</label>
        <label className="flex gap-2"><input type="checkbox" checked={flags.filing} onChange={(event) => setFlags({ ...flags, filing: event.target.checked })} />I understand this submits a live “Notice (generic)” filing to CM/ECF.</label>
        <label className="block font-semibold">Type FILE to confirm
          <input value={confirmation} onChange={(event) => setConfirmation(event.target.value)} className="mt-1 block w-40 rounded border border-slate-300 bg-white px-3 py-2 font-normal" />
        </label>
      </div>
      <button type="button" onClick={start} disabled={working || !filingEnabled || !credentials?.ready_to_file} className="mt-3 rounded bg-red-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
        {working ? 'Starting…' : 'File Notice in CM/ECF'}
      </button>
      {error && <p className="mt-2 text-sm font-medium text-red-700">{error}</p>}
    </details>
  );
}

export default function BNCCompliancePanel({ caseId, compact = false, onChanged }) {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState([]);
  const [manualSelected, setManualSelected] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [serviceDate, setServiceDate] = useState(localDate());
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [deletingPacketId, setDeletingPacketId] = useState('');
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    setError('');
    try {
      const payload = await apiRequest(`/case/${caseId}/bnc-compliance/`);
      setData(payload);
      const pendingIds = payload.requirements.filter((item) => !item.completed_at).map((item) => item.id);
      setSelected((current) => current.length ? current.filter((id) => pendingIds.includes(id)) : pendingIds);
      setRecipients(payload.default_recipients || []);
    } catch (requestError) {
      setError(requestError.message);
    } finally { setLoading(false); }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { if (preview?.url) URL.revokeObjectURL(preview.url); }, [preview]);

  const selectedRequirements = useMemo(
    () => (data?.requirements || []).filter((item) => selected.includes(item.id)),
    [data, selected],
  );

  const toggle = (value, setter) => setter((current) => current.includes(value) ? current.filter((id) => id !== value) : [...current, value]);
  const updateRecipient = (index, field, value) => setRecipients((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, [field]: value } : item));
  const removeRecipient = (index) => setRecipients((current) => current.filter((_item, itemIndex) => itemIndex !== index));

  const previewFile = async (path, label) => {
    setError('');
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(apiUrl(path), { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!response.ok) throw new Error(`Could not open ${label.toLowerCase()} (${response.status}).`);
      const url = URL.createObjectURL(await response.blob());
      setPreview((current) => { if (current?.url) URL.revokeObjectURL(current.url); return { url, label }; });
    } catch (requestError) { setError(requestError.message); }
  };

  const download = async (path, filename) => {
    setError('');
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(apiUrl(path), { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!response.ok) throw new Error(`Download failed (${response.status}).`);
      const url = URL.createObjectURL(await response.blob());
      const link = document.createElement('a'); link.href = url; link.download = filename; document.body.appendChild(link); link.click(); link.remove(); URL.revokeObjectURL(url);
    } catch (requestError) { setError(requestError.message); }
  };

  const generate = async () => {
    setWorking(true); setError('');
    try {
      await apiRequest(`/case/${caseId}/bnc-compliance/generate/`, {
        method: 'POST',
        body: JSON.stringify({ requirement_ids: selected, manual_docket_ids: manualSelected, recipients, service_date: serviceDate }),
      });
      await load(); onChanged?.();
    } catch (requestError) { setError(requestError.message); }
    finally { setWorking(false); }
  };

  const deletePacket = async (packet) => {
    const confirmed = window.confirm(
      'Delete this saved compliance packet and its corresponding todo? This cannot be undone.',
    );
    if (!confirmed) return;
    setDeletingPacketId(packet.id); setError('');
    try {
      await apiRequest(`/case/${caseId}/bnc-compliance/packets/${packet.id}/`, {
        method: 'DELETE',
      });
      await load(); onChanged?.();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setDeletingPacketId('');
    }
  };

  if (loading) return <div className="text-sm text-slate-500">Loading General Order compliance…</div>;
  if (!data) return <div className="text-sm text-red-700">{error || 'The compliance workflow could not be loaded.'}</div>;
  if (!data.requirements.length && !data.other_orders.length && compact) return null;

  return (
    <div id="bnc-compliance" className="space-y-4">
      <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950">
        <div className="font-semibold">General Order 2026-03 — interrupted BNC noticing</div>
        <p className="mt-1">Select the applicable order docket(s), confirm the recipients and service method, then create the Notice of Compliance, order packet, and envelopes. Filing is a separate reviewed action.</p>
      </div>

      <section className="rounded-lg border border-slate-200 bg-white p-4">
        <h4 className="font-semibold text-slate-900">Orders requiring compliance</h4>
        {!data.requirements.length && <p className="mt-2 text-sm text-slate-500">No automatically covered orders were found.</p>}
        <div className="mt-2 space-y-2">
          {data.requirements.map((item) => (
            <label key={item.id} className={`block rounded border p-3 text-sm ${item.completed_at ? 'border-green-200 bg-green-50' : 'border-slate-200'}`}>
              <div className="flex items-start gap-3">
                <input type="checkbox" disabled={Boolean(item.completed_at)} checked={selected.includes(item.id)} onChange={() => toggle(item.id, setSelected)} className="mt-1" />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold">Docket {item.docket.docket_number} · {item.order_type_display}</div>
                  <div className="mt-1 text-slate-600">Entered {formatDate(item.order_entered_on)} · Compliance due {formatDate(item.due_date)}</div>
                  <div className="mt-1 leading-5 text-slate-700">{item.docket.docket_text}</div>
                  {item.docket.has_pdf ? <button type="button" onClick={(event) => { event.preventDefault(); previewFile(item.docket.pdf_url, `Docket ${item.docket.docket_number}`); }} className="mt-2 font-semibold text-indigo-700 hover:underline">View saved order</button> : <div className="mt-2 font-medium text-red-700">Order PDF not saved yet — run Fetch Dockets again.</div>}
                  {item.completed_at && <div className="mt-1 font-semibold text-green-700">Completed</div>}
                </div>
              </div>
            </label>
          ))}
        </div>

        {data.other_orders.length > 0 && (
          <details className="mt-4 rounded border border-slate-200 p-3">
            <summary className="cursor-pointer font-semibold text-slate-800">Include another order manually</summary>
            <p className="mt-1 text-xs text-slate-500">These are other order dockets entered on or after October 7. Include one if its wording differs from the automatic categories.</p>
            <div className="mt-2 max-h-64 space-y-2 overflow-auto">
              {data.other_orders.map((item) => (
                <label key={item.id} className="flex gap-2 rounded border border-slate-100 p-2 text-sm">
                  <input type="checkbox" checked={manualSelected.includes(item.id)} onChange={() => toggle(item.id, setManualSelected)} />
                  <span><strong>Docket {item.docket_number}</strong> · {formatDate(item.entered_on)} · {item.docket_text}</span>
                </label>
              ))}
            </div>
          </details>
        )}
      </section>

      {(selectedRequirements.length > 0 || manualSelected.length > 0) && (
        <section className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="flex flex-wrap items-center justify-between gap-3"><h4 className="font-semibold text-slate-900">Service recipients</h4><label className="text-sm font-semibold">Service date <input type="date" value={serviceDate} onChange={(event) => setServiceDate(event.target.value)} className="ml-2 rounded border border-slate-300 px-2 py-1" /></label></div>
          <div className="mt-3 space-y-3">
            {recipients.map((recipient, index) => (
              <div key={recipient.key || index} className="rounded border border-slate-200 p-3">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <span className="text-sm font-semibold text-slate-700">Recipient {index + 1}</span>
                  <button type="button" onClick={() => removeRecipient(index)} className="rounded border border-red-300 bg-white px-2.5 py-1 text-xs font-semibold text-red-700 hover:bg-red-50">Remove recipient</button>
                </div>
                <div className="grid gap-2 md:grid-cols-3">
                  <input value={recipient.name || ''} onChange={(event) => updateRecipient(index, 'name', event.target.value)} placeholder="Recipient name" className="rounded border border-slate-300 px-3 py-2" />
                  <select value={recipient.method || 'us_mail'} onChange={(event) => updateRecipient(index, 'method', event.target.value)} className="rounded border border-slate-300 px-3 py-2"><option value="us_mail">First-Class U.S. Mail</option><option value="certified_mail">Certified Mail</option><option value="email">Email</option><option value="hand_delivery">Hand Delivery</option></select>
                  <input value={recipient.email || ''} onChange={(event) => updateRecipient(index, 'email', event.target.value)} placeholder="Email" className="rounded border border-slate-300 px-3 py-2" />
                  <input value={recipient.address1 || ''} onChange={(event) => updateRecipient(index, 'address1', event.target.value)} placeholder="Street" className="rounded border border-slate-300 px-3 py-2 md:col-span-2" />
                  <input value={recipient.address2 || ''} onChange={(event) => updateRecipient(index, 'address2', event.target.value)} placeholder="Address line 2" className="rounded border border-slate-300 px-3 py-2" />
                  <input value={recipient.city || ''} onChange={(event) => updateRecipient(index, 'city', event.target.value)} placeholder="City" className="rounded border border-slate-300 px-3 py-2" />
                  <input value={recipient.state || ''} onChange={(event) => updateRecipient(index, 'state', event.target.value)} placeholder="State" className="rounded border border-slate-300 px-3 py-2" />
                  <input value={recipient.zip || ''} onChange={(event) => updateRecipient(index, 'zip', event.target.value)} placeholder="ZIP" className="rounded border border-slate-300 px-3 py-2" />
                </div>
              </div>
            ))}
          </div>
          <button type="button" onClick={() => setRecipients((current) => [...current, { key: `manual:${Date.now()}`, recipient_type: 'other', name: '', address1: '', address2: '', city: '', state: '', zip: '', email: '', method: 'us_mail' }])} className="mt-3 rounded border border-slate-300 bg-white px-3 py-2 text-sm font-semibold">Add another recipient</button>
          <button type="button" onClick={generate} disabled={working || recipients.length === 0} title={recipients.length === 0 ? 'Add at least one service recipient.' : ''} className="ml-2 mt-3 rounded bg-indigo-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{working ? 'Creating…' : 'Create notice, service packet, and envelopes'}</button>
        </section>
      )}

      {data.packets.length > 0 && (
        <section className="rounded-lg border border-slate-200 bg-white p-4"><h4 className="font-semibold text-slate-900">Saved compliance packets</h4><div className="mt-3 space-y-3">{data.packets.map((packet) => (
          <div key={packet.id} className="rounded border border-slate-200 p-3 text-sm">
            <div className="font-semibold">Docket{packet.docket_numbers.length === 1 ? '' : 's'} {packet.docket_numbers.join(', ')} · Served {formatDate(packet.service_date)}</div>
            <div className="mt-1 text-slate-600">{packet.recipient_count} recipient{packet.recipient_count === 1 ? '' : 's'}</div>
            <div className="mt-2 flex flex-wrap gap-2">
              <button type="button" onClick={() => previewFile(packet.notice_url, 'Notice of Compliance')} className="rounded border border-indigo-300 px-3 py-2 font-semibold text-indigo-700">View notice</button>
              <button type="button" onClick={() => download(packet.download_url, `${data.case_number || data.case_id} General Order 2026-03 Packet.zip`)} className="rounded bg-indigo-700 px-3 py-2 font-semibold text-white">Download service packet</button>
              <button
                type="button"
                disabled={!packet.can_delete || deletingPacketId === packet.id}
                title={packet.can_delete ? 'Delete this packet and todo' : 'A packet cannot be deleted after its court filing has started.'}
                onClick={() => deletePacket(packet)}
                className="rounded border border-red-300 bg-white px-3 py-2 font-semibold text-red-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {deletingPacketId === packet.id ? 'Deleting…' : 'Delete packet'}
              </button>
            </div>
            <FilingControls caseId={caseId} packet={packet} filingEnabled={data.filing_enabled} credentials={data.credentials} onChanged={load} />
          </div>
        ))}</div></section>
      )}
      {error && <div className="rounded border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-700">{error}</div>}
      {preview && <div className="fixed inset-0 z-50 flex flex-col bg-black/70 p-3 md:p-8"><div className="flex items-center justify-between rounded-t bg-white px-4 py-3"><strong>{preview.label}</strong><button type="button" onClick={() => setPreview(null)} className="rounded border border-slate-300 px-3 py-1 font-semibold">Close</button></div><iframe title={preview.label} src={preview.url} className="h-full w-full rounded-b bg-white" /></div>}
    </div>
  );
}
